import styled from 'styled-components';

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2.2rem 1.2rem 1.2rem 1.2rem;
  color: var(--text-gray);
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
`;

/* League/cup view: stretch content to edges, more width for competitions */
export const LeagueViewContainer = styled.div`
  max-width: 100%;
  margin: 0;
  padding: 0.75rem 0.5rem 1rem 0.5rem;
  color: var(--text-gray);
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (min-width: 769px) {
    padding: 1rem 0.75rem 1.25rem 0.75rem;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const PageSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-gray);
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 1.2rem;
  text-align: left;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: var(--mobile-paragraph, 0.75rem);
  }
`;

export const FormSection = styled.section`
  margin-bottom: 2rem;
  background: var(--bg-sidebar);
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 1.75rem 1.5rem;
  }
`;

/* Tighter padding when used inside league view */
export const FormSectionLeague = styled(FormSection)`
  padding: 1rem 0.75rem;

  @media (min-width: 769px) {
    padding: 1rem 1rem;
  }
`;

export const FormTitle = styled.h2`
  font-size: 1.4rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  margin-bottom: 2rem;
  text-align: left;
  position: relative;
  padding-left: 1rem;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: var(--mobile-section-title, 1rem);
    margin-bottom: 1rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 6px;
    height: 100%;
    background: var(--accent-orange);
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.7rem 1.5rem;
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 0.7rem;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
`;

export const Label = styled.label`
  font-size: 1rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

export const Input = styled.input`
  padding: 0.4rem 0.7rem;
  font-size: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e0e6ed;
  font-family: 'Poppins', sans-serif;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  color: var(--text-gray);
  appearance: none;
  -webkit-appearance: none;
  min-height: 2.5rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    font-size: 16px; /* prevents iOS zoom on focus */
    padding: 0.5rem 0.7rem;
    text-align: center;
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    appearance: none;
    -webkit-appearance: none;
    color: var(--text-gray);
    -webkit-text-fill-color: var(--text-gray);
    min-height: 2.75rem;
  }
`;

export const Select = styled.select`
  padding: 0.4rem 0.7rem;
  font-size: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e0e6ed;
  font-family: 'Poppins', sans-serif;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  color: var(--text-gray);
  appearance: none;
  -webkit-appearance: none;
  min-height: 2.5rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0.5rem 0.7rem;
    text-align: center;
    text-align-last: center;
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    appearance: none;
    -webkit-appearance: none;
    color: var(--text-gray);
    -webkit-text-fill-color: var(--text-gray);
    min-height: 2.75rem;
  }
`;

export const SubmitButton = styled.button`
  margin-top: 0.7rem;
  padding: 0.6rem 1.25rem;
  border-radius: 12px;
  border: 2px solid var(--accent-orange);
  background: var(--accent-orange);
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  width: 100%;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(255, 150, 0, 0.3);

  &:hover, &:focus {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 150, 0, 0.35);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const SecondaryButton = styled.button`
  margin-top: 0.7rem;
  padding: 0.6rem 1.25rem;
  border-radius: 12px;
  border: 2px solid var(--accent-orange);
  background: transparent;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  width: 100%;
  white-space: nowrap;

  &:hover, &:focus {
    background: var(--accent-orange);
    color: #fff;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const TelemarkToggle = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

export const ToggleButton = styled.button<{ active: boolean }>`
  padding: 0.3rem 1rem;
  border-radius: 1rem;
  border: 1px solid ${props => props.active ? 'var(--accent-orange)' : '#e0e6ed'};
  background: ${props => props.active ? 'var(--accent-orange)' : '#f5f5f5'};
  color: ${props => props.active ? '#fff' : 'var(--text-gray)'};
  font-family: 'Poppins', sans-serif;
  font-size: var(--button-font-size, 0.6rem);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: var(--accent-orange-light);
    color: #fff;
    border-color: var(--accent-orange);
  }
`;

export const LeagueCard = styled.div`
  background: var(--bg-sidebar);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  }
`;

export const LeagueHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

export const LeagueTitle = styled.h3`
  font-size: 1.3rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  margin: 0;
`;

export const LeagueInfo = styled.div`
  display: flex;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

export const CompetitionCard = styled.div<{ $completed?: boolean }>`
  background: #fff;
  border-radius: 0.75rem;
  padding: 0.85rem;
  margin-bottom: 0.75rem;
  border: 2px solid ${props => props.$completed ? 'var(--accent-orange)' : '#e0e6ed'};
  width: 100%;
  box-sizing: border-box;
`;

export const CompetitionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
`;

export const CompetitionTitle = styled.h4`
  font-size: 0.85rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  margin: 0;
`;

export const StandingsTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 0.75rem;
  background: none;
`;

export const TableHeader = styled.th`
  text-align: left;
  padding: 0.5rem 0.65rem;
  color: var(--text-gray);
  font-size: 0.7rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  
  &:first-child {
    padding-left: 0;
  }
  
  &:last-child {
    padding-right: 0;
  }
`;

export const TableCell = styled.td`
  padding: 0.45rem 0.65rem;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  &:first-child {
    padding-left: 0;
  }
  
  &:last-child {
    padding-right: 0;
  }
`;

export const TableRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

export const ErrorMessage = styled.div`
  background: #FFF5F5;
  border: 1px solid #FFE3E3;
  color: #E03131;
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  box-shadow: var(--shadow-sm);
`;

export const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  box-shadow: var(--shadow-sm);
`;

export const LoadingMessage = styled.div`
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
  font-size: 1.1rem;
`;

export const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-gray);
  font-style: italic;
  opacity: 0.7;
`;

export const AthleteCheckbox = styled.label<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  background: ${props => props.selected ? 'var(--accent-orange)' : 'transparent'};
  color: ${props => props.selected ? '#fff' : 'var(--text-gray)'};
  transition: all 0.2s;

  &:hover {
    background: ${props => props.selected ? 'var(--accent-orange-dark)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

export const AthleteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  border: 1px solid #e0e6ed;
  border-radius: 0.5rem;
  background: #fff;
`;

export const InputWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  
  input {
    flex: 1;
    min-width: 0;
  }
`;

export const RandomIconButton = styled.button`
  background: var(--accent-orange);
  color: #fff;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
  font-size: 1.2rem;

  &:hover {
    background: var(--accent-orange-dark);
  }

  &:active {
    transform: scale(0.95);
  }
`;

